import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight, Phone, Sparkles } from 'lucide-react';
// Visually: the "messy garage" photo is the BEFORE state (despuesjr.png by filename),
// and the "clean garage" photo is the AFTER state (antesjr.png by filename).
// We use the visual content as the source of truth for the narrative.
const BEFORE_IMAGE = 'https://cdn.magicpatterns.com/uploads/8XYz1jtkT67XnoKiSBarir/despuesjr.png';
const AFTER_IMAGE = 'https://cdn.magicpatterns.com/uploads/4iubqmtHZJEPkYie7ymi1c/antesjr.png';
export function BeforeAfter() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, x / rect.width * 100));
    setPosition(pct);
  }, []);
  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    updatePosition(e.clientX);
  };
  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    if (e.touches[0]) updatePosition(e.touches[0].clientX);
  };
  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent) => updatePosition(e.clientX);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) updatePosition(e.touches[0].clientX);
    };
    const handleEnd = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, {
      passive: true
    });
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, updatePosition]);
  // Click-to-set on the image itself (not the handle)
  const handleContainerClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    updatePosition(e.clientX);
  };
  return <section id="before-after" className="py-20 lg:py-28 bg-[#F9F9F9] relative overflow-hidden" data-id="element-49" data-mp="17s50ylmf4gr7">
      {/* Decorative red blur */}
      <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#C8102E] opacity-[0.06] blur-[120px] rounded-full pointer-events-none" data-id="element-50" data-mp="1crhz42iqpa93" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-51" data-mp="1guwd6nn4bgjw">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-52" data-mp="1lpa68jeb5ble">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C8102E]/10 border border-[#C8102E]/20 mb-6" data-id="element-53" data-mp="2s770iej3ekgy">
            <Sparkles size={14} className="text-[#C8102E]" data-id="element-54" data-mp="2xjdsryixjdln" />
            <span className="text-[11px] tracking-[0.25em] font-bold uppercase text-[#C8102E]" data-id="element-55" data-mp="2tjvalaihtgmi">
              Real Job. Real Result.
            </span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4" data-id="element-56" data-mp="2jl5p77re1e1i">
            SEE THE <span className="text-[#C8102E]" data-id="element-57" data-mp="cteqo5okfyqy">DIFFERENCE</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600" data-id="element-58" data-mp="8u5vqxhshutt">
            Drag the slider to see what one call to Jose's can do. Same garage.
            One crew. Everything gone.
          </motion.p>
        </div>

        {/* Slider */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white ring-1 ring-black/10" data-id="element-59" data-mp="1wjg4rjfuxub">
          <div ref={containerRef} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} onClick={handleContainerClick} className="relative w-full aspect-[4/3] sm:aspect-[16/10] cursor-ew-resize select-none bg-black" style={{
          touchAction: 'none'
        }} data-id="element-60" data-mp="3acv46z5vu9yl">
            {/* After image (base layer, full visible) */}
            <img src={AFTER_IMAGE} alt="Garage after Jose's Junk Removal — clean and clear" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} data-id="element-61" data-mp="34nznr6kdwtxf" />
            {/* AFTER label */}
            <div className="absolute bottom-4 right-4 bg-white text-[#1A1A1A] px-4 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider shadow-lg z-20 flex items-center gap-2 pointer-events-none" data-id="element-62" data-mp="39nb7mog1sfj8">
              <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-63" data-mp="3jhl2qdwdjrxn"></span>
              After
            </div>

            {/* Before image (clipped overlay) */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{
            width: `${position}%`
          }} data-id="element-64" data-mp="3shhuioe296wx">
              <img src={BEFORE_IMAGE} alt="Garage before Jose's Junk Removal — full of junk and debris" className="absolute inset-0 w-full h-full object-cover" style={{
              width: `${100 / position * 100}%`,
              maxWidth: 'none'
            }} draggable={false} data-id="element-65" data-mp="2v2dcoanx9z5k" />
            </div>
            {/* BEFORE label */}
            <div className="absolute bottom-4 left-4 bg-[#C8102E] text-white px-4 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider shadow-lg z-20 flex items-center gap-2 pointer-events-none transition-opacity" style={{
            opacity: position > 8 ? 1 : 0
          }} data-id="element-66" data-mp="3dtwg2iluzf84">
              <span className="w-2 h-2 rounded-full bg-white" data-id="element-67" data-mp="1sfryvfea6qtl"></span>
              Before
            </div>

            {/* Divider line + handle */}
            <div className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] z-30 pointer-events-none" style={{
            left: `calc(${position}% - 1.5px)`
          }} data-id="element-68" data-mp="1kx2bpf2gk2od">
              {/* Handle */}
              <div onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white shadow-2xl flex items-center justify-center cursor-ew-resize pointer-events-auto ring-4 ring-[#C8102E] ${isDragging ? 'scale-110' : 'hover:scale-105'} transition-transform`} data-id="element-69" data-mp="175noew1ipfbi">
                <ArrowLeftRight size={22} className="text-[#C8102E]" data-id="element-70" data-mp="je8i6s6meduz" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats / breakdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="grid sm:grid-cols-3 gap-4 mt-10" data-id="element-71" data-mp="eh8ciidond4e">
          {[{
          label: 'Crew Size',
          value: '1 Team'
        }, {
          label: 'Time on Site',
          value: '1 Day'
        }, {
          label: 'Your Effort',
          value: 'Zero'
        }].map((stat, i) => <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm" data-id="element-72" data-mp="2nhtas1e0i1cm" data-mpi={i}>
              <div className="text-3xl font-bold text-[#C8102E] mb-1" data-id="element-73" data-mp="3g68omkleqdmd">
                {stat.value}
              </div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold" data-id="element-74" data-mp="3kyik0g5ak5w1">
                {stat.label}
              </div>
            </div>)}
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mt-10" data-id="element-75" data-mp="14ilv2c1ppuq1">
          <p className="text-lg text-gray-700 mb-4 font-medium" data-id="element-76" data-mp="qknklhqiiift">
            Want the same result for your space?
          </p>
          <a href="tel:8312407401" className="inline-flex items-center gap-3 bg-[#C8102E] text-white px-8 py-4 rounded font-bold text-lg hover:bg-red-800 transition-all shadow-[0_8px_24px_rgba(200,16,46,0.35)] hover:-translate-y-0.5" data-id="element-77" data-mp="lmdnh1l897rk">
            <Phone size={22} data-id="element-78" data-mp="2ayfwlfdymwd6" />
            <span data-id="element-79" data-mp="2axmni7rj3urv">Get Your Free Estimate</span>
          </a>
        </motion.div>
      </div>
    </section>;
}